import { encounterField } from '../../shared/array-types';
import type { AppliedFrame, AppliedTouchedRow } from '../transport/applied-frame';
import { applyBasePatch, type BaseEnvelope, type BasePatchOp } from './base-patch';
import { encounterMemberSharedInputs } from './encounter-member-input';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, type FromBaseRow as Row } from './from-base-context';
import type { FromBaseEntryResult } from './from-base-entry';
import { readInventoryField, readInventoryRows } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { phpJsonTruthy } from './php-json-values';

const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const field = (row: Row, key: string): unknown => readInventoryField(row, key);
const own = (row: object, key: string): boolean => Object.prototype.hasOwnProperty.call(row, key);
/** A catalog entry carrying authored layers is a definition; anything else is a name shell. */
const fullDefinition = (entry: unknown): boolean => fromBaseRecord(entry) && fromBaseRecord(entry.item_layer_types)
    && Object.keys(entry.item_layer_types).length > 0;

function adoptFloorEnchantmentNode(held: Row, incoming: AppliedTouchedRow, raw = false): Row {
    const owned = fromBaseClone(raw ? incoming.row : incoming.node!); // dnr-clone: allow size=one incoming saved row or mounted floor subtree; why=each adopter owns only the replacement values
    return raw ? { ...held, inventory: owned } : owned;
}

/** One saved trap mount, decided without yielding: the scoped compile input plus what the install needs. */
export interface FloorEnchantmentMount {
    scoped: BaseEnvelope; ops: readonly BasePatchOp[]; op: BasePatchOp; after: Row; eid: string; entity: string;
    /** The page's computed catalog (encounter envelope only); the facet holds no catalog. */
    held: Row | null;
}

/** A trap mount changes one floor root and its authored closure. Decide that synchronously, so an
 * ordinary patch never waits on this lane, and prepare the subtree compile that retains every other
 * held floor row and the entire resident roster. */
export function floorEnchantmentMount(envelope: BaseEnvelope, ops: readonly BasePatchOp[], computed: unknown): FloorEnchantmentMount | null {
    const writes = ops.filter(op => op.table === 'inventory');
    if (writes.length !== 1 || ops.some(op => !['inventory', 'items', 'item_layer_types'].includes(op.table) || op.op !== 'upsert')) return null;
    const op = writes[0]!, base = envelope.base;
    const eid = upper(encounterField(base, 'encounter_id') ?? encounterField(base.possessor, 'encounter_id'));
    if (eid !== upper(envelope.entity_id) || !Array.isArray(base.inventory)) return null;
    const target = base.inventory.find(row => fromBaseRecord(row) && upper(field(row, 'inventory_id')) === upper(op.row_id));
    if (!target || upper(field(target, 'possess_id')) !== eid) return null;
    const before = target.inventory as Row, after = { ...before, ...op.cols };
    if (!field({ inventory: after }, 'enchantment_template_id') || Object.keys(op.cols).some(key =>
        !['enchantment_template_id', 'inventory_inspection_dc', 'inventory_updated_by_id', 'inventory_t_updated'].includes(key)
        && String(field(target, key) ?? '') !== String(field({ inventory: op.cols }, key) ?? ''))) return null;
    const floor = Array.isArray(computed) ? computed : fromBaseRecord(computed) ? readInventoryRows(computed, 'encounter_inventories_arr') : [];
    if (!floor.some(row => fromBaseRecord(row) && upper(field(row, 'inventory_id')) === upper(op.row_id))) return null;
    const ids = new Set([upper(op.row_id)]);
    const children = new Map<string, Row[]>();
    for (const row of base.inventory) if (fromBaseRecord(row)) {
        const owner = upper(field(row, 'possess_id')), list = children.get(owner) ?? [];
        list.push(row); children.set(owner, list);
    }
    const nodes: Row[] = [];
    const collect = (node: Row): void => {
        nodes.push(node);
        for (const child of children.get(upper(field(node, 'inventory_id'))) ?? []) {
            const id = upper(field(child, 'inventory_id'));
            if (ids.has(id)) throw new Error('floor-enchantment:inventory-cycle');
            ids.add(id); collect(child);
        }
    };
    collect(target);
    const pictures = new Set([...nodes, { inventory: after }].flatMap(row =>
        ['inventory_presentation_pic_id', 'inventory_thumbnail_pic_id'].map(key => upper(field(row, key)))).filter(Boolean));
    const shared = encounterMemberSharedInputs(base);
    const encounter = envelope.entity === 'encounter';
    const scoped: BaseEnvelope = { ...envelope, entity: encounter ? 'encounter-floor' : 'inventory', cache: null,
        base: { ...shared, entity_id: envelope.entity_id, base_rev: envelope.base_rev, inventory: nodes,
            inventory_order: Array.isArray(base.inventory_order) ? base.inventory_order.filter(row => fromBaseRecord(row)
                && ids.has(upper(field({ inventory: row }, 'inventory_id')))) : undefined,
            possessor: base.possessor ?? { encounters: base.encounters }, encounter_links: base.encounter_links,
            picture_lookup_ids: base.picture_lookup_ids, picture_sources: base.picture_sources,
            ...(encounter ? { encounters: base.encounters, floor_order: [op.row_id],
                media_context: Array.isArray(base.media_context) ? base.media_context.filter(row => fromBaseRecord(row)
                    && pictures.has(upper(readItemField(row, 'picture_id')))) : [] } : {}) } };
    if (applyBasePatch(scoped, ops) !== 'applied') return null;
    // Other enchantments can alter encounter terrain; this lane owns trap mounts only.
    const mountedId = upper(field({ inventory: after }, 'enchantment_template_id'));
    const members = Array.isArray(scoped.base.enchantment_template_enchantments)
        ? scoped.base.enchantment_template_enchantments.filter(row => fromBaseRecord(row)
            && upper(readItemField(row, 'enchantment_template_id')) === mountedId) : [];
    if (!members.length || members.some(row => readItemField(row, 'enchantment_type') !== 'trap_ability')) return null;
    // The enclosing compile thins an instance against a template seeded from an unenchanted, unresized
    // instance of the same item; an item whose only instance is this (now enchanted) root is re-derived
    // from its definition and left unthinned. The subtree compile starts from exactly those held
    // templates, and from the held "0" name bucket, so the mounted node thins as the enclosing compile
    // would and no held entry is re-derived.
    const held = encounter ? fromBaseRecord(computed) && fromBaseRecord(computed.catalog) ? computed.catalog : {} : null;
    if (held) {
        const seeding = new Set<string>();
        for (const row of base.inventory) if (fromBaseRecord(row) && upper(field(row, 'inventory_id')) !== upper(op.row_id)
            && !phpJsonTruthy(field(row, 'enchantment_template_id')) && !fromBaseUuid(field(row, 'inventory_size')))
            seeding.add(upper(field(row, 'inventory_item_id')));
        const seed: Row = {};
        for (const node of nodes) {
            const id = upper(field(node, 'inventory_item_id'));
            if (!id || own(seed, id) || !seeding.has(id) || !own(held, id)) continue;
            const entry = held[id];
            // The carried price joins a template only after instance thinning; thin against that shape.
            seed[id] = fromBaseRecord(entry) && fromBaseRecord(entry.computed) && own(entry.computed, 'pl')
                ? { ...entry, computed: Object.fromEntries(Object.entries(entry.computed).filter(([key]) => key !== 'pl')) } : entry;
        }
        if (own(held, '0')) seed['0'] = held['0'];
        if (Object.keys(seed).length) scoped.base.held_catalog = seed;
    }
    return { scoped, ops, op, after, eid, entity: envelope.entity, held };
}

/** Compile the mounted subtree and shape the one-row applied frame. Null leaves the mount to the
 * ordinary lane: a failed subtree compile, or a catalog the lane could only install by replacing a
 * held entry. */
export async function compileFloorEnchantmentMount(mount: FloorEnchantmentMount,
    compile: (input: BaseEnvelope) => FromBaseEntryResult | Promise<FromBaseEntryResult>): Promise<AppliedFrame | null> {
    const { scoped, ops, op, after, eid, entity, held } = mount, encounter = entity === 'encounter';
    let result: FromBaseEntryResult;
    try { result = await compile(scoped); }
    catch { return null; }
    if (result.stagesMissing.length) return null;
    const output = result.computed;
    const floorRows = encounter && fromBaseRecord(output) ? readInventoryRows(output, 'encounter_inventories_arr') : output;
    if (!Array.isArray(floorRows)) return null;
    const node = floorRows.find(row => fromBaseRecord(row) && upper(field(row, 'inventory_id')) === upper(op.row_id));
    if (!fromBaseRecord(node) || !fromBaseRecord(node.inventory)) return null;
    // `node.computed._cache.pl_sig` / `pl_sig_b` are the signatures this subtree compile derived from the
    // same definition inputs. The enclosing compile's values for the same row are a function of the
    // WHOLE floor's finalization order (every root's definition is pre-finalized before any instance,
    // and each first read hashes what was prepared before it), so reproducing them needs a floor-wide
    // pass — a rebuild (measured at 86–100 % of the full compile). They identify a compile for the
    // pricing lane's claims, not a game value; the lane installs its own.
    const catalog = held && fromBaseRecord(output) && fromBaseRecord(output.catalog) ? output.catalog : null;
    if (held && catalog && floorEnchantmentCatalogConflicts(held, catalog).length) return null;
    let sources: Row = {};
    for (const source of ops) if (source.catalog_inputs) sources = mergeInventoryMetadata(sources, source.catalog_inputs);
    const definitions = new Set(ops.filter(source => source.table === 'items' || source.table === 'item_layer_types')
        .map(source => upper(source.row_id)));
    sources.catalog = Object.fromEntries([...definitions].map(id => [id, (scoped.base.catalog as Row)[id]]));
    // Only what the page lacks travels: the mount's new definitions, never a held entry.
    const additions = held && catalog ? floorEnchantmentCatalogAdditions(held, catalog) : null;
    return { kind: 'applied', operation_id: `floor-enchantment:${entity}:${eid}:${op.base_rev}`, encounter_id: eid,
        actor_id: '', rev: 0, member_revs: {}, computed: {}, new_entities: [], deletes: [],
        inputs: { floor_enchantment_sources: encounterMemberSharedInputs(sources),
            ...(encounter ? { floor_enchantment_media: scoped.base.media_context } : {}),
            ...(additions ? { floor_enchantment_catalog: additions } : {}) },
        rows: [{ table: 'inventory', row_id: op.row_id, row: after, node }] };
}

/** The two steps as one call, for callers that need no synchronous decision. */
export async function savedFloorEnchantmentFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[], computed: unknown,
    compile: (input: BaseEnvelope) => FromBaseEntryResult | Promise<FromBaseEntryResult>): Promise<AppliedFrame | null> {
    const mount = floorEnchantmentMount(envelope, ops, computed);
    return mount ? compileFloorEnchantmentMount(mount, compile) : null;
}

/** The entries of `incoming` whose key the held catalog does not have. A held key is never emitted,
 * whatever its shape: the lane adds, it does not replace (names inside a held "0" bucket included —
 * the lean stage itself never extends an existing "0"). */
export function floorEnchantmentCatalogAdditions(held: Row, incoming: Row): Row | null {
    const additions = Object.fromEntries(Object.entries(incoming).filter(([id]) => !own(held, id)));
    return Object.keys(additions).length ? additions : null;
}

/** Held keys the enclosing compile would REPLACE rather than keep: an entry held without authored
 * layers (a name shell, a layerless definition) for which this compile produced a definition with
 * layers. The one-row lane never overwrites a held key, so such a mount is left to the ordinary lane. */
export function floorEnchantmentCatalogConflicts(held: Row, incoming: Row): string[] {
    return Object.keys(incoming).filter(id => id !== '0' && own(held, id) && fullDefinition(incoming[id]) && !fullDefinition(held[id]));
}

/** The held catalog plus those additions on a NEW top-level object: no held key is assigned, so every
 * held entry stays by reference; the held object itself comes back when there is nothing to add. */
export function floorEnchantmentCatalogWith(held: Row, incoming: Row): Row {
    const additions = floorEnchantmentCatalogAdditions(held, incoming);
    return additions ? { ...held, ...additions } : held;
}

export function floorEnchantmentOnlyRow(frame: AppliedFrame): AppliedTouchedRow | null {
    if (!frame.operation_id.startsWith('floor-enchantment:') || frame.rows.length !== 1 || frame.new_entities.length
        || frame.deletes.length || frame.new_inventory?.length || Object.keys(frame.computed).length
        || Object.keys(frame.member_revs).length || frame.computed_delta && Object.keys(frame.computed_delta).length) return null;
    const row = frame.rows[0]!;
    return row.table === 'inventory' && fromBaseRecord(row.node) && upper(field(row.node, 'inventory_id')) === upper(row.row_id)
        && fromBaseRecord(frame.inputs?.floor_enchantment_sources) ? row : null;
}

/** Both worker memo and page installer use the same one-root splice. */
export function patchFloorEnchantment(source: unknown, frame: AppliedFrame, row: AppliedTouchedRow, raw: boolean): unknown {
    if (Array.isArray(source)) return source.map(node => fromBaseRecord(node)
        && upper(field(node, 'inventory_id')) === upper(row.row_id) ? adoptFloorEnchantmentNode(node, row) : node);
    if (!fromBaseRecord(source)) return source;
    const key = raw ? 'inventory' : fromBaseRecord(source.encounters) ? 'encounter_inventories_arr' : 'inventory';
    if (!Array.isArray(source[key])) return source;
    const next = raw ? mergeInventoryMetadata(source, frame.inputs!.floor_enchantment_sources) : { ...source };
    if (raw && Array.isArray(frame.inputs?.floor_enchantment_media)) {
        const media = new Map((Array.isArray(source.media_context) ? source.media_context : []).filter(fromBaseRecord)
            .map(row => [upper(readItemField(row, 'picture_id')), row]));
        for (const row of frame.inputs.floor_enchantment_media.filter(fromBaseRecord)) media.set(upper(readItemField(row, 'picture_id')), row);
        next.media_context = [...media.values()];
    }
    if (!raw && fromBaseRecord(frame.inputs?.floor_enchantment_catalog))
        next.catalog = floorEnchantmentCatalogWith(fromBaseRecord(source.catalog) ? source.catalog : {}, frame.inputs.floor_enchantment_catalog);
    next[key] = (source[key] as Row[]).map(node => upper(field(node, 'inventory_id')) === upper(row.row_id)
        ? adoptFloorEnchantmentNode(node, row, raw) : node);
    return next;
}
